import React, { useContext, useEffect, useRef, useState } from 'react'
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import { DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import { formatDateDDMMMYYYY } from 'Common/HelperClass';
import IterationOverflow from './IterationOverflow';
import { useHistory } from 'react-router-dom';
import { deleteIterationMethod, fetchIterationTableData } from '../../services/IterationServices';
import usePagination from 'Components/Common/hooks/usePagination';
import CSTablePagination from '../CSTablePagination';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { toast } from 'react-toastify';
import { IterationContext, IterationContextType } from '../../contexts/IterationContext';
import { ConfirmationDialog } from 'Pages/GMS/ISR/UI-Model/components/Common/ConfirmationDialog';

type DeleteConfirmation = {
    iteration: IterationTableItem | null;
    isOpen: boolean;
};

const IterationTable = () => {
    const [sortBy, setSortBy] = useState<IterationSortOption>('category_name');
    const [sortOrder, setSortOrder] = useState<1 | -1>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [noPermission, setNoPermission] = useState<boolean>(false);
    const [iterationMethods, setIterationMethods] = useState<IterationTableItem[] | undefined>(undefined);
    const [selectIndex, setSelectIndex] = useState<number | undefined>(undefined);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>({
        iteration: null,
        isOpen: false
    });
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const history = useHistory();
    const { updatePermission, deletePermission, industryId, channelKey } = useContext<IterationContextType>(IterationContext);
    const { totalRecords, handlePageChange, updatePageSize, paginationParams, updateTotalRecords } = usePagination();

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setSelectIndex(undefined);
            }
        }
        function handleScroll() {
            if (selectIndex != null) {
                setSelectIndex(undefined);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [selectIndex]);

    useEffect(() => {
        fetchData();
    }, [sortBy, sortOrder, paginationParams, channelKey]);

    const fetchData = async () => {
        try {
            setLoading(true);
            if (!industryId || !channelKey) return
            const response = await fetchIterationTableData(
                industryId,
                channelKey,
                paginationParams.page,
                paginationParams.per_page,
                sortBy,
                sortOrder
            );
            if (response?.message === "Permission denied!!") {
                setNoPermission(true);
            } else {
                setNoPermission(false);
            }

            setIterationMethods(response?.data ?? undefined);
            updateTotalRecords(response?.pagination?.total ?? 0);
        } catch (e) {
            toast.error(e as string);
        }
        finally {
            setLoading(false);
        }
    }

    const handleSort = (column: IterationSortOption) => {
        const isSameColumn = column === sortBy;
        const newSortOrder = isSameColumn ? (sortOrder === 1 ? -1 : 1) : 1;

        setSortBy(column);
        setSortOrder(newSortOrder);
    }

    const handleEdit = (iteration: IterationTableItem) => {
        if (!updatePermission) { toast.error("You don't have permission to edit"); return; }
        history.push(`/gms/computed_substitutions_iteration/${iteration?.id}/edit`);
    }

    const handleDelete = (iteration: IterationTableItem) => {
        if (!deletePermission) {
            toast.error("You don't have permission to delete");
            return;
        }

        setDeleteConfirmation({
            iteration,
            isOpen: true
        });
    }

    const confirmDelete = async () => {
        if (!deleteConfirmation.iteration) return;

        try {
            setIsDeleting(true);
            const res = await deleteIterationMethod(deleteConfirmation.iteration.id);
            if (res?.done) {
                toast.success("Computed substitution deleted successfully");
                fetchData();
            } else {
                toast.warning(res?.message || "Delete operation failed");
            }
        } catch (error) {
            toast.error("Failed to delete computed substitution");
            console.error("Delete error:", error);
        } finally {
            setIsDeleting(false);
            setDeleteConfirmation({
                iteration: null,
                isOpen: false
            });
        }
    }

    const cancelDelete = () => {
        setDeleteConfirmation({
            iteration: null,
            isOpen: false
        });
    }

    const rowPadding = 'tw-py-2';

    const headerClasses = "tw-cursor-pointer tw-py-3 tw-text-left tw-text-sm tw-font-semibold tw-text-[#6B778C] tw-tracking-wider tw-whitespace-nowrap";
    const truncatedCell = "tw-truncate tw-max-w-0 tw-overflow-hidden tw-whitespace-nowrap";

    const getColumnWidth = (column: IterationSortOption): string => {
        switch (column) {
            case "iterations":
                return `tw-w-[${20}%]`;
            case "updated_at":
                return `tw-w-[${20}%]`;
            case "category_name":
                return `tw-w-[${30}%]`;
            case "created_by":
                return `tw-w-[${25}%]`;
            default:
                return `tw-w-[2%]`;
        }
    };

    const renderHeaderCell = (column: IterationSortOption, label: string, indent: boolean = false) => (
        <th key={column} className={`${headerClasses} ${getColumnWidth(column)}`} onClick={() => handleSort(column)}>
            <div className={`tw-flex tw-items-center ${indent ? 'tw-ml-12' : ''}`}>
                <span>{label}</span>
                <span className="tw-ml-1">{sortElement(column)}</span>
            </div>
        </th>
    );

    const sortElement = (column: string) => {
        return sortBy === column ? (
            <span className="tw-ml-1">
                {sortOrder === 1 ? (
                    <i className="fas fa-sort-up" />
                ) : (
                    <i className="fas fa-sort-down" />
                )}
            </span>
        ) : (
            <i className="fas fa-sort" />
        );
    };

    const handleOverflowClick = (event: React.MouseEvent<HTMLDivElement>, index: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom - window.scrollY,
            left: rect.left - window.scrollX,
        });
        setSelectIndex(index);
    };

    if (loading) { return <div className=""><TableSkeletonElement /></div> }
    if (noPermission && !loading && !iterationMethods?.length) return <NoDataComponent title='Permission Denied!' message="Looks like you don't have enough permission to access this feature or resource." />
    if (!loading && !iterationMethods?.length) return <NoDataComponent />

    return (
        <>
            <div className="tw-border tw-border-solid tw-rounded-md tw-mt-3 tw-border-[#DFE0E6] tw-overflow-hidden tw-mb-4">
                <div className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                    <table className="tw-bg-zinc-200 tw-w-full tw-table-fixed">
                        <thead className="tw-sticky tw-top-0 tw-bg-zinc-200">
                            <tr>
                                {renderHeaderCell("category_name", "Category", true)}
                                {renderHeaderCell("iterations", "Iterations")}
                                {renderHeaderCell("created_by", "Created by")}
                                {renderHeaderCell("updated_at", "Last updated on")}
                                <th key={'overflow'} className={`${headerClasses} ${getColumnWidth('id')}`}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {iterationMethods?.map((iteration, index) => {
                                return (
                                    <React.Fragment key={iteration.id}>
                                        <tr
                                            key={iteration.id}
                                            className="hover:tw-bg-gray-50"
                                        >
                                            <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"} tw-pl-12 tw-text-blue-800 tw-text-sm tw-font-normal`} title={iteration?.category_name}>{iteration?.category_name}</td>
                                            <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"} tw-text-blue-950 tw-text-sm tw-font-normal`} >{iteration?.iterations}</td>
                                            <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"} tw-text-blue-950 tw-text-sm tw-font-normal`} title={iteration?.created_by}>{iteration?.created_by}</td>
                                            <td className={`${rowPadding} ${truncatedCell} ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"} tw-text-blue-950 tw-text-sm tw-font-normal`}>{formatDateDDMMMYYYY(iteration?.updated_at)}</td>
                                            <td className={`tw-w-2 ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"} tw-cursor-pointer`}
                                                onClick={(e) => {
                                                    if (index === selectIndex) setSelectIndex(undefined);
                                                    else { handleOverflowClick(e, index); }
                                                }}>
                                                <OverFlowIcon />
                                                {selectIndex === index ? (
                                                    <div ref={menuRef}
                                                        className="tw-fixed tw-rounded-[4px] tw-z-50  tw-bg-white tw-ring-1 tw-ring-[#EBECF0]"
                                                        style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left + 40}px` }}>
                                                        <div className={`tw-origin-top-left tw-mr-8 tw-right-0 tw-absolute  tw-mx-3 tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}>
                                                            {IterationOverflow(iteration, <EditIcon />, "Edit", () => { setSelectIndex(undefined); handleEdit(iteration) })}
                                                            {IterationOverflow(iteration, <DeleteIcon />, "Delete", () => { setSelectIndex(undefined); handleDelete(iteration) })}
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {!loading && iterationMethods && iterationMethods.length > 0 && (
                <CSTablePagination
                    paginationParams={paginationParams}
                    totalRecords={totalRecords}
                    updatePageSize={updatePageSize}
                    handlePageChange={handlePageChange}
                />
            )}

            <ConfirmationDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Computed Substitution"
                message="Are you sure you want to delete this computed substitution?"
                secondaryMessage='This action cannot be undone.'
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                isLoading={isDeleting}
            />
        </>
    );
};

export default IterationTable;